import { useQuery } from "@apollo/client";
import { isEmpty, map, orderBy, upperCase } from "lodash";
import React, { useReducer } from "react";
import PropTypes from "prop-types";
import { Box } from "orcs-design-system";
import { searchAppliedTags } from "src/queries/tags.graphql";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import TagsHierarchyViewer from "./components/TagsHierarchyViewer";
import { TagsHierarchyViewerContext } from "./context";
import { tagsReducer, initialTagsState } from "./reducers/tagsReducer";
import { setTagsData, setError } from "./actions/tagsActions";

const TagsHierarchyViewerContainer = ({
  entityId,
  tagTypes,
  entityType = ENTITY_TYPES.GROUP,
  showContainer,
}) => {
  const [state, dispatch] = useReducer(tagsReducer, initialTagsState);
  const { tags, loading: stateLoading, error: stateError } = state;

  useQuery(searchAppliedTags, {
    variables: {
      entityIds: [entityId],
      tagTypes: map(tagTypes, "id"),
      appliedToTypes: [upperCase(entityType)],
      size: 1000,
      offset: 0,
    },
    skip: !entityId || isEmpty(tagTypes),
    onCompleted: (response) => {
      if (!isEmpty(response?.results?.entityTags)) {
        dispatch(
          setTagsData(
            orderBy(map(response?.results?.entityTags, "tag"), "displayValue")
          )
        );
      }
    },
    onError: (queryError) => {
      dispatch(setError(queryError));
    },
  });

  if (!entityId || isEmpty(tagTypes)) {
    return null;
  }

  if ((!stateLoading && isEmpty(tags)) || stateError) {
    return null;
  }

  if (!showContainer) {
    return (
      <Box width="100%">
        <TagsHierarchyViewerContext.Provider
          value={{
            tags,
            tagTypes,
            loading: stateLoading,
            error: stateError,
            dispatch,
          }}
        >
          <TagsHierarchyViewer />
        </TagsHierarchyViewerContext.Provider>
      </Box>
    );
  }

  return (
    <Box
      borderRadius={2}
      boxBorder="default"
      p="r"
      bg="white"
      width="100%"
      minWidth={0}
      overflow="hidden"
    >
      <Box width="100%">
        <TagsHierarchyViewerContext.Provider
          value={{
            tags,
            tagTypes,
            loading: stateLoading,
            error: stateError,
            dispatch,
          }}
        >
          <TagsHierarchyViewer />
        </TagsHierarchyViewerContext.Provider>
      </Box>
    </Box>
  );
};

TagsHierarchyViewerContainer.propTypes = {
  tagTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  entityId: PropTypes.string.isRequired,
  showContainer: PropTypes.bool,
  entityType: PropTypes.string,
};

export default TagsHierarchyViewerContainer;
